<template>
  <cst-page-template class="media-page" ds-root :hide-menu="true">
    <header-menu />
    <div class="ds-page ds-page--fill">
      <div class="ds-page__header">
        <h1>
          Media beheer
          <button
            class="ds-info-button"
            title="Informatie over media beheer"
            aria-label="Informatie over media beheer"
            data-qa="media-info-button"
            @click="showInfoModal = true"
          >
            <ds-icon name="info" />
          </button>
        </h1>
      </div>
      <p class="ds-page__intro">
        Beheer logo's, footer-afbeeldingen en algemene voorwaarden per dealer en vestiging.
      </p>

      <div v-if="loading" class="ds-empty">
        <p class="ds-empty__message">Media laden…</p>
      </div>

      <div v-else-if="!items.length" class="ds-empty">
        <h2 class="ds-empty__title">Geen media gevonden</h2>
        <p class="ds-empty__message">Er is nog geen media geüpload.</p>
      </div>

      <!-- Importers manage every dealer's media; dealers only their own types. -->
      <media-table
        v-else
        :items="items"
        :columns="isImporter ? importerColumns : dealerColumns"
        :expanded-item="expandedItem"
        :history="history"
        :history-loading="historyLoading"
        :default-sort-column="isImporter ? 'dealerId' : null"
        @view-file="openViewModal"
        @upload-request="triggerUpload"
        @toggle-history="toggleHistory"
        @restore-version="restore"
      />
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg"
      class="media-page__hidden-input"
      @change="onFileChosen"
    />

    <ds-dialog
      v-if="showInfoModal"
      id="media-info"
      title="Media beheer"
      size="md"
      @close="showInfoModal = false"
    >
      <div class="media-page__info" v-html="i18n.t('/media-page/dealer/tooltip')"></div>
      <template #footer>
        <ds-button data-qa="media-info-close-button" @click="showInfoModal = false">
          Sluiten
        </ds-button>
      </template>
    </ds-dialog>

    <media-preview-modal
      v-if="viewMedia.show"
      :sas-url="viewMedia.sasUrl"
      :label="viewMedia.label"
      @close="closeViewModal"
      @download="downloadMedia"
    />

    <media-upload-preview-modal
      v-if="showPreviewModal"
      :media-type-label="pendingUpload ? formatMediaType(pendingUpload.item.mediaType) : ''"
      :preview-url="previewUrl"
      :loading="previewLoading"
      @confirm="confirmUpload"
      @cancel="cancelPreview"
    />

    <div v-if="successMessage || uploadWarning || error" class="ds-toast-host">
      <div v-if="successMessage" class="ds-toast ds-toast--success">
        <span>{{ successMessage }}</span>
        <button class="ds-toast__close" aria-label="Sluiten" @click="successMessage = null">
          &times;
        </button>
      </div>
      <div v-if="uploadWarning" class="ds-toast ds-toast--warning">
        <span>{{ uploadWarning }}</span>
        <button class="ds-toast__close" aria-label="Sluiten" @click="uploadWarning = null">
          &times;
        </button>
      </div>
      <div v-if="error" class="ds-toast ds-toast--error">
        <span>{{ error }}</span>
        <button class="ds-toast__close" aria-label="Sluiten" @click="error = null">&times;</button>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import MediaTable from './MediaTable.vue'
import MediaPreviewModal from './MediaPreviewModal.vue'
import MediaUploadPreviewModal from './MediaUploadPreviewModal.vue'
import mediaService from '@/services/mediaService'
import { formatMediaType, MEDIA_TYPE_LABELS } from './mediaConstants'
import { mapGetters, mapState } from 'vuex'

const MAX_SIZE_BYTES = 1.5 * 1024 * 1024
const WARN_SIZE_BYTES = 1024 * 1024

export default {
  name: 'MediaPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    DsButton,
    DsIcon,
    DsDialog,
    MediaTable,
    MediaPreviewModal,
    MediaUploadPreviewModal,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      // Server state
      loading: false,
      items: [],
      expandedItem: null,
      history: [],
      historyLoading: false,
      // Upload flow
      uploadTarget: null,
      pendingUpload: null,
      showPreviewModal: false,
      previewUrl: null,
      previewLoading: false,
      // UI state
      showInfoModal: false,
      viewMedia: { show: false, sasUrl: null, blobName: null, label: null },
      // Toasts
      error: null,
      uploadWarning: null,
      successMessage: null,
    }
  },
  computed: {
    ...mapState('establishments', ['establishments']),
    ...mapGetters({ dealerSettings: 'user/dealerSettings' }),
    currentEstablishmentId() {
      return (
        this.$route.params.establishmentId || this.establishments?.[0]?.EstablishmentNumber || null
      )
    },
    isImporter() {
      return this.dealerSettings.isImporter
    },
    mediaTypeOptions() {
      return Object.entries(MEDIA_TYPE_LABELS).map(([value, label]) => ({ value, label }))
    },
    importerColumns() {
      return [
        {
          key: 'dealerId',
          label: 'Dealer ID',
          width: '140px',
          filter: 'text',
          placeholder: 'Zoek Dealer ID...',
        },
        {
          key: 'establishmentId',
          label: 'Establishment ID',
          width: '175px',
          filter: 'text',
          placeholder: 'Zoek Establishment ID...',
        },
        {
          key: 'mediaType',
          label: 'Type',
          width: '150px',
          filter: 'select',
          options: this.mediaTypeOptions,
        },
        {
          key: 'blobName',
          label: 'Huidig bestand',
          width: '300px',
          filter: 'text',
          placeholder: 'Zoek bestand...',
        },
      ]
    },
    dealerColumns() {
      return [
        {
          key: 'mediaType',
          label: 'Type',
          width: '200px',
          filter: 'select',
          options: this.mediaTypeOptions,
        },
        {
          key: 'blobName',
          label: 'Huidig bestand',
          width: '320px',
          filter: 'text',
          placeholder: 'Zoek bestand...',
        },
      ]
    },
  },
  watch: {
    currentEstablishmentId() {
      this.loadItems()
    },
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    formatMediaType,

    // ── Helpers ───────────────────────────────────────────────────────────────
    itemKey(item) {
      return `${item.dealerId}-${item.establishmentId}-${item.mediaType}`
    },
    showSuccess(message) {
      this.successMessage = message
      setTimeout(() => {
        this.successMessage = null
      }, 4000)
    },

    // ── Data loading ──────────────────────────────────────────────────────────
    async loadItems() {
      this.loading = true
      this.error = null
      try {
        const response = await mediaService.getMediaItems(this.currentEstablishmentId)
        this.items = response.data || []
      } catch (e) {
        console.error('Failed to load media items:', e)
        this.error = 'Laden van media mislukt. Probeer opnieuw.'
      } finally {
        this.loading = false
      }
    },

    // ── Upload flow ───────────────────────────────────────────────────────────
    triggerUpload(item) {
      this.uploadTarget = item
      const input = this.$refs.fileInput
      input.value = ''
      input.click()
    },
    onFileChosen(event) {
      const file = event.target.files[0]
      if (file) this.onFileSelected({ item: this.uploadTarget, file })
    },
    async onFileSelected({ item, file }) {
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        this.error = 'Alleen PNG en JPEG bestanden zijn toegestaan.'
        return
      }
      if (file.size > MAX_SIZE_BYTES) {
        this.error = 'Bestand is te groot (max 1,5 MB).'
        return
      }
      this.uploadWarning =
        file.size > WARN_SIZE_BYTES
          ? `Let op: bestand is groter dan 1 MB (${(file.size / 1024 / 1024).toFixed(2)} MB).`
          : null
      this.pendingUpload = { file, item }
      await this.openPreview()
    },
    async openPreview() {
      this.showPreviewModal = true
      this.previewLoading = true
      this.previewUrl = null
      try {
        const response = await mediaService.previewMediaItem(
          this.pendingUpload.item.mediaType,
          this.pendingUpload.file
        )
        const blob = new Blob([response.data], { type: 'text/html' })
        this.previewUrl = URL.createObjectURL(blob)
      } catch (e) {
        console.error('Preview failed:', e)
        this.error = 'Voorbeeld laden mislukt. Probeer opnieuw.'
        this.showPreviewModal = false
      } finally {
        this.previewLoading = false
      }
    },
    async confirmUpload() {
      const { file, item } = this.pendingUpload
      try {
        await mediaService.uploadMediaItem(
          item.mediaType,
          file,
          item.establishmentId,
          item.dealerId
        )
        await this.loadItems()
        this.showSuccess('Bestand succesvol geüpload.')
      } catch (e) {
        console.error('Upload failed:', e)
        this.error = 'Uploaden mislukt. Probeer opnieuw.'
      } finally {
        this.cancelPreview()
      }
    },
    cancelPreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.showPreviewModal = false
      this.previewUrl = null
      this.pendingUpload = null
    },

    // ── History ───────────────────────────────────────────────────────────────
    async toggleHistory(item) {
      const key = this.itemKey(item)
      if (this.expandedItem === key) {
        this.expandedItem = null
        this.history = []
        return
      }
      this.expandedItem = key
      this.history = []
      this.historyLoading = true
      try {
        const response = await mediaService.getMediaHistory(
          item.mediaType,
          item.establishmentId,
          item.dealerId
        )
        this.history = response.data || []
      } catch (e) {
        console.error('Failed to load history:', e)
        this.error = 'Versiegeschiedenis laden mislukt.'
      } finally {
        this.historyLoading = false
      }
    },
    async restore(item, versionId) {
      try {
        await mediaService.restoreMediaVersion(
          item.mediaType,
          versionId,
          item.establishmentId,
          item.dealerId
        )
        await this.loadItems()
        this.expandedItem = null
        this.history = []
        this.showSuccess('Versie succesvol hersteld.')
      } catch (e) {
        console.error('Restore failed:', e)
        this.error = 'Herstellen mislukt. Probeer opnieuw.'
      }
    },

    // ── View / download ───────────────────────────────────────────────────────
    openViewModal(item) {
      this.viewMedia = {
        show: true,
        sasUrl: item.sasUrl,
        blobName: item.blobName,
        label: formatMediaType(item.mediaType),
      }
    },
    closeViewModal() {
      this.viewMedia = { show: false, sasUrl: null, blobName: null, label: null }
    },
    async downloadMedia() {
      try {
        const response = await fetch(this.viewMedia.sasUrl)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = this.viewMedia.blobName || 'media'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch {
        window.open(this.viewMedia.sasUrl, '_blank')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The page chrome, table, buttons, dialogs and toasts all come
// from the design system (styles/ds/). See styles/ds/README.md before adding
// anything here.

.media-page__hidden-input {
  display: none;
}

// The info dialog body comes from i18n as raw HTML, hence :deep().
.media-page__info {
  :deep(p) {
    margin: 0 0 12px;
  }

  :deep(ul) {
    margin: 0 0 14px;
    padding-left: 20px;
  }

  :deep(li) {
    margin-bottom: 6px;
  }

  :deep(strong) {
    color: var(--ds-color-text);
  }
}
</style>
